import type { ReferencedEntry } from 'contentful';
import { createHookContext } from '@clover-util/react-context';
import { useCallback, useState } from 'react';
import { getField } from 'utils/EntryUtil';
import { useContentfulResellerQuery } from 'hooks/Contentful/useContentfulResellerQuery';
import { useDCSReseller } from 'hooks/useDCSReseller';
import { getPartnerKey, resetReferralHostIdIfDeleted } from './util';
import { useResellerStorage } from './useResellerStorage';
import { useProgramPromotion } from 'hooks/ProgramPromotion/useProgramPromotion';
import { useResellerTemplate } from 'hooks/Reseller/useResellerTemplate';

/**
 * This hook exposes information about the current reseller.
 * The contentfulReseller comes from Contentful, which is implemented in useContentfulResellerQuery(), and is returned by the hook
 * Additionally, hook returns subfields on the contentful entry: `resellerId`, `resellerRedirectUrl`, `brandingBanner`, `phoneNumber`
 * The dcsReseller comes from DCS, which is implemented in useDCSReseller()
 * The hook returns fields on the dcsReseller: `referralId`, `isDemoAvailable`, `isMsrpEnabled`, `supportedInstallmentPlans`
 * `referralHostId` is a state variable, initialized via `getPartnerKey`
 * `isDisallowedByReseller` is a function that returns true if a contentful entry is disallowed by the reseller
 * (based on the entry's `disallowedResellers` field)
 */
export const { ResellerProvider, useReseller } = createHookContext('Reseller', () => {
  const [referralHostId, setReferralHostId] = useState(getPartnerKey());

  const { data: contentfulReseller, error: contentfulResellerError } =
    useContentfulResellerQuery(referralHostId);
  const resellerId = getField(contentfulReseller, 'resellerId');
  const resellerRedirectUrl = getField(contentfulReseller, 'redirectUrl');
  const brandingBanner = getField(contentfulReseller, 'brandingBanner');
  const phoneNumber = getField(contentfulReseller, 'phoneNumber');
  const referrerDomains = getField(contentfulReseller, 'referrerDomains') ?? [];

  const { reseller: dcsReseller, getResellerError: dcsResellerError } = useDCSReseller(resellerId);
  const resellerChannel = dcsReseller?.resellerChannel;
  const referralIdFromDCS = dcsReseller?.referralId;
  const isDemoAvailable = !!dcsReseller?.isDemoAvailable;
  const isMsrpEnabled = !!dcsReseller?.isMsrpEnabled;
  const supportedInstallmentPlans = dcsReseller?.supportedInstallmentPlans;

  useResellerStorage(contentfulReseller, referralIdFromDCS);
  const { programPromotion, isProgramPromoLoading } = useProgramPromotion(resellerId);
  const resellerTemplate = useResellerTemplate(phoneNumber, programPromotion);

  resetReferralHostIdIfDeleted(setReferralHostId, referralHostId);

  const isDisallowedByReseller = useCallback(
    (entry?: ReferencedEntry<any>) => {
      const disallowedResellers = getField(entry, 'disallowedResellers') as string[] | undefined;

      if (!disallowedResellers) return false;

      return !!(referralIdFromDCS && disallowedResellers.includes(referralIdFromDCS));
    },
    [referralIdFromDCS],
  );

  return {
    resellerId,
    contentfulReseller,
    contentfulResellerError,
    resellerRedirectUrl,
    brandingBanner,
    phoneNumber,
    referrerDomains,
    dcsResellerError,
    referralId: referralIdFromDCS,
    isDemoAvailable,
    isMsrpEnabled,
    supportedInstallmentPlans,
    referralHostId,
    setReferralHostId,
    isDisallowedByReseller,
    programPromotion,
    isProgramPromoLoading,
    resellerTemplate,
    resellerChannel,
  };
});
